/**
 * @file React hook — useA1FormData.ts: form/state logic for contributor dashboard.
 */

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useA1Prefill } from "./useA1Prefill";
import { useA1EnergyCalculator } from "./useA1EnergyCalculator";
import { FORM_STAGE_CODES } from "../../../../../../constants/formStages";
import { resolveKgCo2eUnitId, resolveUnitSelectValueFromLabel } from "../../shared/unitSelectOptions";
import {
  ExtractionProcessOption,
  FormDataResponse,
  FormSubSection,
} from "../../../../../../interfaces/formInterfaces";
import {
  fetchActiveCharacterizationFactors,
  type CharacterizationFactorPublic,
} from "../../../../../../services/characterizationFactorsPublicService";
import {
  fetchActiveCalorificValues,
  type CalorificValuePublic,
} from "../../../../../../services/calorificValuesPublicService";
import {
  getA1FormData,
  getExtractionProcessesByMaterial,
  getFuelDensities,
  getSubmission as getA1Submission,
  saveA1Details,
  type FuelDensityApiRow,
} from "../../../../../../services/a1Service";
import { reverseGeocode } from "../../../../../../utils/useA1Location";
import { toast } from "react-toastify";
import {
  getFirstFormError,
  validateA1Form,
} from "../../shared/validateA1Form";
import { clearFieldErrorsFromMap } from "../../shared/contributorFormValidation";
import { fetchUnitIdToSymbolMap } from "../../../../../../utils/fetchUnitIdToSymbolMap";
import {
  perFinishedCarbonUnitLabel,
  perFinishedCarbonUnitValue,
  perFinishedEnergyUnitLabel,
  perFinishedEnergyUnitValue,
  resolveFinishedQtyDenominatorSymbol,
} from "../../A1-A3/a1a3SummaryFromSubmission";
import {
  normalizeEmbodiedCarbonFunctionalUnitFormValue,
  resolveEmbodiedCarbonFunctionalUnitForSave,
} from "../../shared/contributorPerFunctionalUnitUnits";
import {
  formatEmbodiedCarbonReferenceQty,
  formatEmbodiedCarbonReferenceQtyFromUnknown,
  formatEmbodiedCarbonFunctionalUnit,
  formatEmbodiedCarbonFunctionalUnitFromUnknown,
  roundEmbodiedCarbonReferenceQty,
} from "../../shared/embodiedCarbonReferenceQtyFormat";
import axiosClient from "../../../../../../api/axiosClient";
import { API_URLS } from "../../../../../../constants/apiUrls";
import {
  fetchCatalogRawMaterialsForMaterial,
  normalizeRawMaterialNameKey,
  resolveCatalogRawMaterialIdFromBackgroundRow,
} from "../../shared/catalogRawMaterialResolution";
import {
  buildA1EmbodiedEnergyValueUpdates,
  buildDefaultA1EmbodiedEnergyUnitUpdates,
  classifyFuelMixForA1EmbodiedEnergy,
  computeA1EmbodiedCarbonReferenceKgFromFuelRow,
  resolveA1EmbodiedEnergyMjUnitSelectValue,
  resolveA1UnitLabelFromValue,
} from "../../../../../../utils/a1FuelEmbodiedEnergy";
import {
  applyFuelDensityUnitFieldChange,
  filterFuelDensityUnitOptionsToKgPerM3,
  fuelDensityValueAndUnitFromCatalogRow,
  getFuelMixDropdownLabelForField,
  isFuelDensityUnitFormFieldName,
  pairedFuelDensityValueKey,
} from "../../shared/fuelMixReferenceAutofill";
import { formatUnitOptionLabel } from "../../../../../../utils/formatUnitSymbol";

/* ======================================================
   CHARACTERIZATION FACTOR LOOKUP
====================================================== */
const FUEL_CHARACTERIZATION_FACTORS: Record<
  string,
  { value: number; unit: string }
> = {
  coal: { value: 95, unit: "kg CO2/GJ" },
  diesel: { value: 74, unit: "kg CO2/GJ" },
  biofuel: { value: 56, unit: "kg CO2/GJ" },
  petrol: { value: 77, unit: "kg CO2/GJ" },
  electric: { value: 0.7, unit: "kg CO2/kWh" },
};

const normalizeFuelKey = (v?: string) =>
  v ? v.toLowerCase().replace(/\s+/g, "_") : "";

function parseLeadingNumberFromDisplay(s: string): number | null {
  const t = String(s ?? "").trim();
  const m = t.match(/^[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

function findPublicFactorForFuelMix(
  fuelMix: string | undefined,
  factors: CharacterizationFactorPublic[]
): CharacterizationFactorPublic | undefined {
  if (!factors.length) return undefined;
  const sel = String(fuelMix ?? "").trim();
  if (!sel) return undefined;
  const key = normalizeFuelKey(sel);
  const exact = factors.find((f) => normalizeFuelKey(f.fuelMix) === key);
  if (exact) return exact;
  const lower = sel.toLowerCase();
  return factors.find((f) => {
    const fk = (f.fuelMix || "").toLowerCase().trim();
    return (
      fk === lower ||
      fk.includes(lower) ||
      lower.includes(fk) ||
      normalizeFuelKey(f.fuelMix) === key
    );
  });
}

function normalizeUnitToken(s: unknown) {
  const raw = String(s ?? "").trim().toLowerCase();
  if (!raw) return "";
  const fixed = raw
    .replaceAll("co₂", "co2")
    .replaceAll("₂", "2")
    .replace(/\s+/g, "");
  return fixed.replace(/[^a-z0-9/()+\-.]/g, "");
}

function resolveUnitValueForCarbonField(
  carbon: FormSubSection,
  unitFieldName: string,
  desiredUnitToken: string,
  unitIdByToken: Record<string, number>
): string {
  const unitField = carbon.fields?.find((f: any) => f?.name === unitFieldName);
  const opts = unitField?.dropdown?.options || unitField?.dropdownValues || [];
  return resolveUnitSelectValueFromLabel(
    desiredUnitToken,
    unitIdByToken,
    Array.isArray(opts) ? opts : []
  );
}

function buildCharacterizationFactorUpdates(
  currentValues: Record<string, string>,
  fuel: FormSubSection,
  carbon: FormSubSection,
  rows: number,
  apiFactors: CharacterizationFactorPublic[],
  manualOverrides: Record<string, boolean>,
  unitIdByToken: Record<string, number>,
  hardcoded: typeof FUEL_CHARACTERIZATION_FACTORS
): Record<string, string> {
  const fuelCode = fuel.code!;
  const carbonCode = carbon.code!;
  const updates: Record<string, string> = {};

  for (let i = 0; i < rows; i++) {
    const fuelMix = currentValues[`fuelMix_extraction_${fuelCode}_row${i}`];
    const rowKey = `row${i}`;
    if (manualOverrides[rowKey]) continue;

    const key = normalizeFuelKey(fuelMix);
    const unitValueKey = `characterizationFactor_unit_extraction_${carbonCode}_row${i}`;
    const cfKey = `characterizationFactor_extraction_${carbonCode}_row${i}`;

    let numeric: number | null = null;
    let unitStr = "";

    if (apiFactors.length > 0) {
      const pub = findPublicFactorForFuelMix(fuelMix, apiFactors);
      if (pub) {
        numeric = parseLeadingNumberFromDisplay(pub.characterizationFactor);
        unitStr = String(pub.unit ?? "").trim();
      }
    }

    if (numeric == null || !Number.isFinite(numeric)) {
      const factor = hardcoded[key];
      if (!factor) continue;
      numeric = factor.value;
      unitStr = factor.unit;
    }

    updates[cfKey] = String(numeric);

    const resolved = resolveUnitValueForCarbonField(
      carbon,
      "characterizationFactor_unit_extraction",
      unitStr,
      unitIdByToken
    );
    if (resolved && currentValues[unitValueKey] !== resolved) {
      updates[unitValueKey] = resolved;
    }
  }

  return updates;
}

type RawMaterialValue =
  | string
  | { id: number; name: string }
  | null
  | undefined;

/** Parse `fuelMix_extraction_<sectionCode>_rowN` (section code may contain underscores). */
function parseFuelMixFieldName(name: string): { code: string; row: string } | null {
  const m = name.match(/^fuelMix_extraction_(.+)_(row\d+)$/);
  if (!m) return null;
  return { code: m[1], row: m[2] };
}

function findFuelDensityForMix(
  mixValue: string,
  mixDropdownLabel: string,
  rows: FuelDensityApiRow[]
): FuelDensityApiRow | null {
  const v = String(mixValue ?? "").trim();
  if (!v) return null;

  const byId = rows.find((r) => String(r.id) === v);
  if (byId) return byId;

  const labelNorm = normalizeFuelKey(mixDropdownLabel);
  const valueNorm = normalizeFuelKey(v);

  for (const r of rows) {
    const fnKey = normalizeFuelKey(r.fuelName);
    if (fnKey && (fnKey === valueNorm || fnKey === labelNorm)) return r;
    const fnLower = r.fuelName.trim().toLowerCase();
    if (fnLower === v.toLowerCase()) return r;
    if (labelNorm && (fnKey.includes(labelNorm) || labelNorm.includes(fnKey)))
      return r;
    if (valueNorm && (fnKey.includes(valueNorm) || valueNorm.includes(fnKey)))
      return r;
  }
  return null;
}

function getFuelMixDropdownLabel(
  formData: FormDataResponse | null,
  mixValue: string
): string {
  const fuelSec = formData?.formSubSections?.find(
    (s) => s.code === "A1_FUEL_EXTRACTION"
  );
  const field = fuelSec?.fields?.find((f) => f.name === "fuelMix_extraction");
  const opts = field?.dropdown?.options || field?.dropdownValues || [];
  const hit = opts.find(
    (o: { value?: string | number; label?: string }) =>
      String(o.value) === String(mixValue)
  );
  return String(hit?.label ?? hit?.value ?? mixValue ?? "");
}

function findCalorificValueForMix(
  mixValue: string,
  mixDropdownLabel: string,
  rows: CalorificValuePublic[]
): CalorificValuePublic | null {
  const v = String(mixValue ?? "").trim();
  if (!v) return null;

  const byId = rows.find((r) => String(r.id) === v);
  if (byId) return byId;

  const labelNorm = normalizeFuelKey(mixDropdownLabel);
  const valueNorm = normalizeFuelKey(v);

  for (const r of rows) {
    const fmKey = normalizeFuelKey(r.fuelMix);
    if (fmKey && (fmKey === valueNorm || fmKey === labelNorm)) return r;
    const fmLower = r.fuelMix.trim().toLowerCase();
    if (fmLower === v.toLowerCase()) return r;
    if (labelNorm && (fmKey.includes(labelNorm) || labelNorm.includes(fmKey)))
      return r;
    if (valueNorm && (fmKey.includes(valueNorm) || valueNorm.includes(fmKey)))
      return r;
  }
  return null;
}

/** Same shape as A1 select options (value = unit id or dropdown value). */
interface UnitSelectOption {
  value: string;
  label: string;
}

/** Load options for fuel density / calorific unit fields (UNIT → /units/property, else CMS dropdown). */
async function loadFuelFieldUnitOptions(
  fuelSub: FormSubSection | undefined,
  fieldName: string
): Promise<UnitSelectOption[]> {
  const f = fuelSub?.fields?.find((x) => x.name === fieldName);
  if (!f) return [];

  if (f.type === "UNIT" && f.unitPropertyId) {
    try {
      const res = await axiosClient.get(
        API_URLS.UNITS.BY_PROPERTY(f.unitPropertyId)
      );
      const raw = res.data?.data;
      const units = Array.isArray(raw) ? raw : [];
      const mapped = units
        .filter((u: { id?: unknown }) => u?.id != null)
        .map((u: Record<string, unknown>) => ({
          value: String(u.id),
          label: formatUnitOptionLabel({
            symbol: u.symbol as string | undefined,
            unitSymbol: u.unitSymbol as string | undefined,
            name: u.name as string | undefined,
            displayName: u.displayName as string | undefined,
            id: u.id as number | string | undefined,
          }),
        }));
      return isFuelDensityUnitFormFieldName(fieldName)
        ? filterFuelDensityUnitOptionsToKgPerM3(mapped)
        : mapped;
    } catch {
      return [];
    }
  }

  const opts =
    f.dropdown?.options ||
    f.dropdownValues ||
    ([] as { value?: string | number; label?: string }[]);
  const mapped = opts.map((o) => ({
    value: String(o.value ?? ""),
    label: formatUnitOptionLabel({ symbol: o.label, name: o.label }),
  }));
  return isFuelDensityUnitFormFieldName(fieldName)
    ? filterFuelDensityUnitOptionsToKgPerM3(mapped)
    : mapped;
}

function pickFuelDensityUnitFromOptions(
  options: UnitSelectOption[],
  mode: "liter" | "m3"
): string {
  if (!options.length) return "";

  const literRe =
    /(kg\s*\/?\s*l|kilogram.*lit|per\s*l\b|litre|liter|^\s*l\s*$)/i;
  const m3Re =
    /(kg\s*\/?\s*m[³3]|kg\/m3|m[³3]\b|per\s*m[³3]|cubic\s*met|cubic\s*meter)/i;

  if (mode === "liter") {
    let hit = options.find((o) => literRe.test(o.label));
    if (hit) return hit.value;
    for (const o of options) {
      const n = normalizeUnitToken(o.label);
      if (n.includes("kg") && n.includes("l") && !n.includes("m3")) {
        return o.value;
      }
    }
    return "";
  }

  let hit = options.find((o) => m3Re.test(o.label));
  if (hit) return hit.value;
  for (const o of options) {
    const n = normalizeUnitToken(o.label);
    if (
      (n.includes("kg") && n.includes("m3")) ||
      n.includes("m3") ||
      /cubic/.test(o.label.toLowerCase())
    ) {
      return o.value;
    }
  }
  return "";
}

function optionListAcceptsValue(
  options: UnitSelectOption[],
  value: string
): boolean {
  if (!value) return false;
  return options.some((o) => String(o.value) === String(value));
}

/** Map API calorific `unit` to option value using the same list the fuel section renders. */
function pickCalorificUnitFromOptions(
  apiUnit: string,
  options: UnitSelectOption[],
  tokenMap: Record<string, number>
): string {
  const u = String(apiUnit ?? "").trim();
  if (!u) return "";

  if (options.length) {
    const target = normalizeUnitToken(u);
    for (const o of options) {
      const lbl = normalizeUnitToken(o.label);
      const val = normalizeUnitToken(o.value);
      if (target && (lbl === target || val === target)) return o.value;
      if (
        target &&
        lbl &&
        (lbl.includes(target) || target.includes(lbl))
      ) {
        return o.value;
      }
    }
    const lower = u.toLowerCase();
    for (const o of options) {
      const lbl = o.label.toLowerCase();
      if (
        lbl === lower ||
        lbl.includes(lower) ||
        lower.includes(lbl)
      ) {
        return o.value;
      }
    }
  }

  const tryId = (idStr: string) =>
    idStr && (!options.length || optionListAcceptsValue(options, idStr))
      ? idStr
      : "";

  for (const part of u
    .split(/[/\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean)) {
    const p = part.toLowerCase();
    const id = tokenMap[p] ?? tokenMap[normalizeUnitToken(p)];
    if (id != null) {
      const s = tryId(String(id));
      if (s) return s;
    }
  }
  const id2 =
    tokenMap[u.toLowerCase()] ?? tokenMap[normalizeUnitToken(u)];
  if (id2 != null) {
    const s = tryId(String(id2));
    if (s) return s;
  }
  return "";
}

const normalizeRawMaterial = (
  value: RawMaterialValue
): { id: number | null; name: string | null } => {
  if (!value) {
    return { id: null, name: null };
  }

  // Case 1: object (future-proof)
  if (typeof value === "object") {
    return {
      id: value.id ?? null,
      name: value.name ?? null,
    };
  }

  // Case 2: string (current implementation)
  const num = Number(value);
  return {
    id: isNaN(num) ? null : num,
    name: isNaN(num) ? value : null,
  };
};

/** Hook: use A1Form Data. */
export function useA1FormData({ submissionId }: { submissionId?: string }) {
  const isMounted = useRef(true);

  /** 🔐 Manual override registry (row-based) */
  /** User edited CF (or unit): do not overwrite from fuel mix; still use their CF in energy×CF. */
  const cfManualFromUserRef = useRef<Record<string, boolean>>({});
  /** User edited reference/functional carbon outputs: do not auto-overwrite those fields. */
  const embodiedCarbonDerivedManualRef = useRef<Record<string, boolean>>({});

  /** Reference rows from GET /fuel-densities (fuel mix → density auto-fill). */
  const fuelDensitiesRef = useRef<FuelDensityApiRow[]>([]);
  const [fuelDensityCatalog, setFuelDensityCatalog] = useState<FuelDensityApiRow[]>(
    []
  );
  const [calorificValuesCatalog, setCalorificValuesCatalog] = useState<
    CalorificValuePublic[]
  >([]);

  /** Reference rows from GET /calorific-values (fuel mix → calorific auto-fill). */
  const calorificValuesRef = useRef<CalorificValuePublic[]>([]);

  /** Unit options for A1 fuel section (same source as A1Section UNIT fields). */
  const fuelSectionUnitOptionsRef = useRef<{
    density: UnitSelectOption[];
    calorific: UnitSelectOption[];
  }>({ density: [], calorific: [] });

  /** Valid `raw_materials.id` values for this submission's finished material. */
  const catalogRawMaterialIdsRef = useRef<Set<number>>(new Set());
  const catalogNameToIdRef = useRef<Map<string, number>>(new Map());

  // Unit lookup (token -> unitId) to convert unit symbols like "kWh"/"MJ" to numeric IDs for the API.
  const unitIdByTokenRef = useRef<Record<string, number>>({});
  /** Inverse map (unitId -> token) for resolving numeric unit dropdown values in calculations. */
  const unitTokenByIdRef = useRef<Record<string, string>>({});

  // Save/dirty tracking (same UX as Contributor Details)
  const lastSavedRef = useRef<Record<string, string>>({});
  const snapshotInitializedRef = useRef(false);
  const prefillDoneRef = useRef(false);
  const [hasSavedOnce, setHasSavedOnce] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const shallowEqual = (a: Record<string, string>, b: Record<string, string>) => {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if (a[k] !== b[k]) return false;
    }
    return true;
  };

  const [formData, setFormData] = useState<FormDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  /** Background `finishedQuantity` — declared quantity of finished construction material (from submission). */
  const [backgroundDeclaredMaterialQuantity, setBackgroundDeclaredMaterialQuantity] =
    useState<number | null>(null);

  /** Background finished material unit symbol (e.g. kg) from subMaterialUnit. */
  const [backgroundFinishedMaterialUnitSymbol, setBackgroundFinishedMaterialUnitSymbol] =
    useState<string | null>(null);

  const [rawMaterials, setRawMaterials] = useState<
    {
      id: number;
      /** `raw_materials.id` for A1 API FK; null if only background junction id was known. */
      catalogId: number | null;
      name: string;
      qty?: string;
      unitId?: number;
    }[]
  >([]);

  /** Finished-material extraction processes (Background materialId). */
  const [extractionProcesses, setExtractionProcesses] = useState<
    ExtractionProcessOption[]
  >([]);

  const [sectionRows, setSectionRows] = useState<Record<string, number>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  /** Bumps after GET /units index loads so embodied-energy unit ids resolve for fuel/default sync */
  const [unitsIndexTick, setUnitsIndexTick] = useState(0);

  const [characterizationFactors, setCharacterizationFactors] = useState<
    CharacterizationFactorPublic[]
  >([]);

  const { prefillFromSubmission } = useA1Prefill();

  const { energySummaryMj, computeAndSetEnergySummary } =
    useA1EnergyCalculator({
      formData,
      values,
      sectionRows,
    });

  const perFinishedEnergyUnitLabelDisplay = useMemo(
    () => perFinishedEnergyUnitLabel(backgroundFinishedMaterialUnitSymbol),
    [backgroundFinishedMaterialUnitSymbol]
  );

  const perFinishedCarbonUnitLabelDisplay = useMemo(
    () => perFinishedCarbonUnitLabel(backgroundFinishedMaterialUnitSymbol),
    [backgroundFinishedMaterialUnitSymbol]
  );

  /** Sum of row embodied carbon (reference raw material qty); same keys as payload `referenceQty`. */
  const embodiedCarbonRowSum = useMemo(() => {
    if (!formData) return 0;

    const carbonSec = formData.formSubSections?.find(
      (s) => s.code === "A1_EMBODIED_CARBON"
    );
    const energySec = formData.formSubSections?.find(
      (s) => s.code === "A1_EMBODIED_ENERGY"
    );
    if (!carbonSec?.code) return 0;

    const carbonCode = carbonSec.code;
    const energyCode = energySec?.code;
    const rows =
      (energyCode && sectionRows[energyCode]) ||
      sectionRows[carbonCode] ||
      1;

    let sumRef = 0;
    for (let i = 0; i < rows; i++) {
      const refKey = `embodiedCarbon_referenceQty_extraction_${carbonCode}_row${i}`;
      const n = Number(values[refKey]);
      if (Number.isFinite(n)) sumRef += n;
    }

    return Math.round(sumRef * 1000) / 1000;
  }, [formData, sectionRows, values]);

  const energyPerDeclaredFinishedMaterialMj = useMemo(() => {
    const q = backgroundDeclaredMaterialQuantity;
    if (q == null || q <= 0) return null;
    return Math.round((energySummaryMj / q) * 1000) / 1000;
  }, [energySummaryMj, backgroundDeclaredMaterialQuantity]);

  const embodiedCarbonPerFinishedKg = useMemo(() => {
    const q = backgroundDeclaredMaterialQuantity;
    if (q == null || q <= 0) return null;
    if (embodiedCarbonRowSum <= 0) return null;
    return Math.round((embodiedCarbonRowSum / q) * 1000) / 1000;
  }, [embodiedCarbonRowSum, backgroundDeclaredMaterialQuantity]);

  const getEmbodiedEnergyMjUnitId = useCallback(
    () => resolveA1EmbodiedEnergyMjUnitSelectValue(unitIdByTokenRef.current),
    []
  );

  /* ======================================================
     AUTO CHARACTERIZATION FACTOR
  ====================================================== */
  const autoSetCharacterizationFactor = useCallback(
    (currentValues: Record<string, string>) => {
      try {
        if (!formData) return;

        const fuel = formData.formSubSections?.find(
          (s) => s.code === "A1_FUEL_EXTRACTION"
        );
        const carbon = formData.formSubSections?.find(
          (s) => s.code === "A1_EMBODIED_CARBON"
        );
        if (!fuel || !carbon) return;

        const fuelCode = fuel.code!;
        const rows = sectionRows[fuelCode] || 1;

        const updates = buildCharacterizationFactorUpdates(
          currentValues,
          fuel,
          carbon,
          rows,
          characterizationFactors,
          cfManualFromUserRef.current,
          unitIdByTokenRef.current,
          FUEL_CHARACTERIZATION_FACTORS
        );

        if (Object.keys(updates).length) {
          setValues((p) => ({ ...p, ...updates }));
        }
      } catch (err) {
        console.error("❌ Auto CF failed:", err);
      }
    },
    [formData, sectionRows, characterizationFactors]
  );

  // When API characterization factors load after prefill, apply fuel-mix → CF mapping.
  useEffect(() => {
    if (!formData || characterizationFactors.length === 0) return;
    const fuel = formData.formSubSections?.find(
      (s) => s.code === "A1_FUEL_EXTRACTION"
    );
    const carbon = formData.formSubSections?.find(
      (s) => s.code === "A1_EMBODIED_CARBON"
    );
    if (!fuel || !carbon) return;
    const fuelCode = fuel.code!;
    const rows = sectionRows[fuelCode] || 1;

    setValues((prev) => {
      const updates = buildCharacterizationFactorUpdates(
        prev,
        fuel,
        carbon,
        rows,
        characterizationFactors,
        cfManualFromUserRef.current,
        unitIdByTokenRef.current,
        FUEL_CHARACTERIZATION_FACTORS
      );
      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
  }, [characterizationFactors, formData, sectionRows, unitsIndexTick]);

  /* ======================================================
     AUTO EMBODIED CARBON CALCULATION
  ====================================================== */
  const autoCalculateEmbodiedCarbon = useCallback(
    (currentValues: Record<string, string>) => {
      try {
        if (!formData) return;

        const fuelSec = formData.formSubSections?.find(
          (s) => s.code === "A1_FUEL_EXTRACTION"
        );
        const energySec = formData.formSubSections?.find(
          (s) => s.code === "A1_EMBODIED_ENERGY"
        );
        const carbonSec = formData.formSubSections?.find(
          (s) => s.code === "A1_EMBODIED_CARBON"
        );
        if (!energySec || !carbonSec) return;

        const fuelCode = fuelSec?.code;
        const energyCode = energySec.code!;
        const carbonCode = carbonSec.code!;
        const rows = sectionRows[energyCode] || 1;

        const cfUnitField = carbonSec.fields?.find(
          (f) => f.name === "characterizationFactor_unit_extraction"
        );
        const cfUnitOpts =
          cfUnitField?.dropdown?.options || cfUnitField?.dropdownValues || [];

        const qtyUnitField = fuelSec?.fields?.find(
          (f) => f.name === "fuelQuantity_unit_extraction"
        );
        const qtyUnitOpts =
          qtyUnitField?.dropdown?.options || qtyUnitField?.dropdownValues || [];

        const densityUnitField = fuelSec?.fields?.find(
          (f) => f.name === "fuelDensity_unit_extraction"
        );
        const densityUnitOpts =
          densityUnitField?.dropdown?.options ||
          densityUnitField?.dropdownValues ||
          [];

        const unitTokenById = unitTokenByIdRef.current;
        const updates: Record<string, string> = {};

        for (let i = 0; i < rows; i++) {
          const rowKey = `row${i}`;
          if (embodiedCarbonDerivedManualRef.current[rowKey]) continue;

          const energy = Number(
            currentValues[`embodiedEnergy_extraction_${energyCode}_row${i}`]
          );
          const cf = Number(
            currentValues[
              `characterizationFactor_extraction_${carbonCode}_row${i}`
            ]
          );
          const declaredQty = Number(rawMaterials[i]?.qty);

          if (!Number.isFinite(cf) || cf <= 0) continue;

          const cfUnitRaw =
            currentValues[
              `characterizationFactor_unit_extraction_${carbonCode}_row${i}`
            ] ?? "";
          let cfUnitLabel = resolveA1UnitLabelFromValue(
            cfUnitRaw,
            cfUnitOpts,
            unitTokenById
          );

          let referenceQty: number | null = null;

          if (fuelCode) {
            const fuelMix = String(
              currentValues[`fuelMix_extraction_${fuelCode}_row${i}`] ?? ""
            ).trim();
            if (fuelMix) {
              const mixLabel = getFuelMixDropdownLabelForField(
                formData,
                fuelCode,
                "fuelMix_extraction",
                fuelMix
              );
              const category = classifyFuelMixForA1EmbodiedEnergy(
                fuelMix,
                mixLabel
              );
              const fuelQty = Number(
                String(
                  currentValues[
                    `fuelQuantity_extraction_${fuelCode}_row${i}`
                  ] ?? ""
                )
                  .replace(/,/g, "")
                  .trim()
              );
              const calorific = Number(
                String(
                  currentValues[
                    `calorificValue_extraction_${fuelCode}_row${i}`
                  ] ?? ""
                )
                  .replace(/,/g, "")
                  .trim()
              );
              const qtyUnitRaw =
                currentValues[
                  `fuelQuantity_unit_extraction_${fuelCode}_row${i}`
                ] ?? "";
              const densityRaw =
                currentValues[
                  `fuelDensity_extraction_${fuelCode}_row${i}`
                ] ?? "";
              const densityUnitRaw =
                currentValues[
                  `fuelDensity_unit_extraction_${fuelCode}_row${i}`
                ] ?? "";
              const densityParsed = Number(
                String(densityRaw).replace(/,/g, "").trim()
              );

              if (!cfUnitLabel) {
                const pub = findPublicFactorForFuelMix(
                  fuelMix,
                  characterizationFactors
                );
                if (pub?.unit) {
                  cfUnitLabel = String(pub.unit).trim();
                } else {
                  const factor =
                    FUEL_CHARACTERIZATION_FACTORS[normalizeFuelKey(fuelMix)];
                  if (factor) cfUnitLabel = factor.unit;
                }
              }

              referenceQty = computeA1EmbodiedCarbonReferenceKgFromFuelRow({
                category,
                fuelQty,
                calorificValueMjPerKg: calorific,
                cfValue: cf,
                cfUnitLabel,
                energyMj: Number.isFinite(energy) ? energy : undefined,
                extras: {
                  quantityUnitValue: qtyUnitRaw,
                  quantityUnitLabel: resolveA1UnitLabelFromValue(
                    qtyUnitRaw,
                    qtyUnitOpts,
                    unitTokenById
                  ),
                  fuelDensity: Number.isFinite(densityParsed)
                    ? densityParsed
                    : undefined,
                  densityUnitLabel: resolveA1UnitLabelFromValue(
                    densityUnitRaw,
                    densityUnitOpts,
                    unitTokenById
                  ),
                },
              });
            }
          }

          if (
            referenceQty == null &&
            Number.isFinite(energy) &&
            Number.isFinite(cf) &&
            energy > 0
          ) {
            referenceQty = roundEmbodiedCarbonReferenceQty(energy * cf * 1000);
          }

          if (referenceQty == null || !Number.isFinite(referenceQty)) continue;

          referenceQty = roundEmbodiedCarbonReferenceQty(referenceQty);

          const refKey = `embodiedCarbon_referenceQty_extraction_${carbonCode}_row${i}`;
          const fnKey = `embodiedCarbon_functionalUnit_extraction_${carbonCode}_row${i}`;
          const refUnitKey = `embodiedCarbon_referenceQty_unit_extraction_${carbonCode}_row${i}`;
          const fnUnitKey = `embodiedCarbon_functionalUnit_unit_extraction_${carbonCode}_row${i}`;
          const kgCo2eUnitId = resolveKgCo2eUnitId(unitIdByTokenRef.current);
          const refStr = formatEmbodiedCarbonReferenceQty(referenceQty);
          if (currentValues[refKey] !== refStr) {
            updates[refKey] = refStr;
          }
          if (kgCo2eUnitId && currentValues[refUnitKey] !== kgCo2eUnitId) {
            updates[refUnitKey] = kgCo2eUnitId;
          }

          if (Number.isFinite(declaredQty) && declaredQty > 0) {
            const fnStr = formatEmbodiedCarbonFunctionalUnit(
              referenceQty / declaredQty
            );
            if (currentValues[fnKey] !== fnStr) {
              updates[fnKey] = fnStr;
            }
            const fnUnitVal = backgroundFinishedMaterialUnitSymbol?.trim()
              ? perFinishedCarbonUnitValue(backgroundFinishedMaterialUnitSymbol)
              : kgCo2eUnitId;
            if (fnUnitVal && currentValues[fnUnitKey] !== fnUnitVal) {
              updates[fnUnitKey] = fnUnitVal;
            }
          }
        }

        if (Object.keys(updates).length) {
          setValues((p) => ({ ...p, ...updates }));
        }
      } catch (err) {
        console.error("❌ Auto carbon calc failed:", err);
      }
    },
    [formData, sectionRows, rawMaterials, characterizationFactors, backgroundFinishedMaterialUnitSymbol]
  );

  /* ======================================================
     EMBODIED ENERGY UNIT — default MJ (dropdown is MJ/kWh tokens only)
     Deps: only embodied-unit fields, not all `values` (avoids resetting on unrelated edits).
  ====================================================== */
  const a1EmbodiedEnergyUnitSig = (() => {
    if (!formData) return "";
    const energySec = formData.formSubSections?.find(
      (s) => s.code === "A1_EMBODIED_ENERGY"
    );
    if (!energySec?.code) return "";
    const code = energySec.code;
    const n = sectionRows[code] ?? 1;
    let s = "";
    for (let i = 0; i < n; i++) {
      s += `${values[`embodiedEnergy_unit_extraction_${code}_row${i}`] ?? ""}\u001f`;
    }
    return s;
  })();

  useLayoutEffect(() => {
    if (!formData) return;
    const updates = buildDefaultA1EmbodiedEnergyUnitUpdates(
      formData,
      values,
      sectionRows,
      { mjUnitId: getEmbodiedEnergyMjUnitId() }
    );
    if (!Object.keys(updates).length) return;
    let changed = false;
    for (const k of Object.keys(updates)) {
      if (values[k] !== updates[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    setValues((v) => ({ ...v, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `a1EmbodiedEnergyUnitSig` tracks only unit fields; full `values` read from latest render
  }, [a1EmbodiedEnergyUnitSig, formData, sectionRows, unitsIndexTick, getEmbodiedEnergyMjUnitId]);

  /* ======================================================
     EMBODIED ENERGY (A1) FROM FUEL — auto-calc embodied energy in MJ
  ====================================================== */
  const a1FuelEnergySyncFingerprint = (() => {
    if (!formData) return "";
    const fuelCode = "A1_FUEL_EXTRACTION";
    const n = sectionRows[fuelCode] ?? 1;
    let s = "";
    for (let i = 0; i < n; i++) {
      s += `${values[`fuelMix_extraction_${fuelCode}_row${i}`] ?? ""}\u001f${
        values[`fuelQuantity_extraction_${fuelCode}_row${i}`] ?? ""
      }\u001f${
        values[`fuelQuantity_unit_extraction_${fuelCode}_row${i}`] ?? ""
      }\u001f${
        values[`fuelDensity_extraction_${fuelCode}_row${i}`] ?? ""
      }\u001f${
        values[`fuelDensity_unit_extraction_${fuelCode}_row${i}`] ?? ""
      }\u001f${values[`calorificValue_extraction_${fuelCode}_row${i}`] ?? ""}|`;
    }
    return s;
  })();

  useEffect(() => {
    if (!formData) return;
    const updates = buildA1EmbodiedEnergyValueUpdates(
      formData,
      values,
      sectionRows,
      {
        mjUnitId: getEmbodiedEnergyMjUnitId(),
        unitTokenById: unitTokenByIdRef.current,
      }
    );
    let changed = false;
    for (const k of Object.keys(updates)) {
      if (values[k] !== updates[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    setValues((v) => ({ ...v, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when fuel inputs change; `values` read from latest render via fingerprint
  }, [a1FuelEnergySyncFingerprint, formData, sectionRows, unitsIndexTick, getEmbodiedEnergyMjUnitId]);

  useEffect(() => {
    if (!formData) return;
    autoCalculateEmbodiedCarbon(values);
  }, [values, formData, autoCalculateEmbodiedCarbon, unitsIndexTick]);

  useLayoutEffect(() => {
    if (!formData || !backgroundFinishedMaterialUnitSymbol?.trim()) return;
    const carbonCode = "A1_EMBODIED_CARBON";
    const n = sectionRows[carbonCode] ?? 1;
    const kgCo2eUnitId = resolveKgCo2eUnitId(unitIdByTokenRef.current);
    const perUnit = perFinishedCarbonUnitValue(backgroundFinishedMaterialUnitSymbol);
    if (!perUnit) return;

    setValues((prev) => {
      const updates: Record<string, string> = {};
      for (let i = 0; i < n; i++) {
        const unitKey = `embodiedCarbon_functionalUnit_unit_extraction_${carbonCode}_row${i}`;
        const normalized = normalizeEmbodiedCarbonFunctionalUnitFormValue(
          prev[unitKey] ?? "",
          backgroundFinishedMaterialUnitSymbol,
          kgCo2eUnitId
        );
        if (normalized && prev[unitKey] !== normalized) {
          updates[unitKey] = normalized;
        }
      }
      if (!Object.keys(updates).length) return prev;
      return { ...prev, ...updates };
    });
  }, [
    formData,
    backgroundFinishedMaterialUnitSymbol,
    sectionRows,
    unitsIndexTick,
  ]);

  /* ======================================================
     LOAD FORM + PREFILL
  ====================================================== */
// ONLY THE IMPORTANT PARTS SHOWN FULLY (no omissions)

useEffect(() => {
  isMounted.current = true;

  const fetchForm = async () => {
    setLoading(true);
    try {
      // reset tracking for new load
      snapshotInitializedRef.current = false;
      prefillDoneRef.current = false;
      lastSavedRef.current = {};
      setIsDirty(false);
      setHasSavedOnce(false);
      catalogRawMaterialIdsRef.current = new Set();
      catalogNameToIdRef.current = new Map();

      // Load units index in parallel (best-effort; does not block form usage)
      try {
        const unitsRes = await axiosClient.get("/units");
        const raw = unitsRes?.data?.data ?? unitsRes?.data;

        const map: Record<string, number> = {};
        const byId: Record<string, string> = {};
        const normalizeToken = (s: unknown) => {
          const rawStr = String(s ?? "").trim().toLowerCase();
          if (!rawStr) return "";
          const fixed = rawStr
            .replaceAll("co₂", "co2")
            .replaceAll("₂", "2")
            .replace(/\s+/g, "");
          return fixed.replace(/[^a-z0-9/()+\-.]/g, "");
        };
        const addToken = (token: unknown, id: unknown) => {
          const t = String(token ?? "").trim().toLowerCase();
          const tn = normalizeToken(token);
          const n = Number(id);
          if (!t) return;
          if (!Number.isFinite(n)) return;
          if (!map[t]) map[t] = n;
          if (tn && !map[tn]) map[tn] = n;
        };

        const consumeUnit = (u: any) => {
          if (!u) return;
          const id = u.id;
          addToken(u.symbol, id);
          addToken(u.unitSymbol, id);
          addToken(u.name, id);
          addToken(u.displayName, id);
          if (id != null) {
            const idStr = String(id);
            const pick =
              String(u.symbol ?? u.unitSymbol ?? "").trim() ||
              String(u.name ?? u.displayName ?? "").trim();
            if (pick && !byId[idStr]) byId[idStr] = pick;
          }
        };

        // Shape A: [{ id, units: [...] }, ...]
        if (Array.isArray(raw)) {
          raw.forEach((propertyOrUnit: any) => {
            if (Array.isArray(propertyOrUnit?.units)) {
              propertyOrUnit.units.forEach(consumeUnit);
            } else {
              consumeUnit(propertyOrUnit);
            }
          });
        } else if (raw && Array.isArray((raw as any).units)) {
          // Shape B: { units: [...] }
          (raw as any).units.forEach(consumeUnit);
        }

        unitIdByTokenRef.current = map;
        unitTokenByIdRef.current = byId;
        if (isMounted.current) setUnitsIndexTick((t) => t + 1);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[A1] failed to load units index; unit IDs may be null", e);
      }

      try {
        const cfList = await fetchActiveCharacterizationFactors();
        if (isMounted.current) setCharacterizationFactors(cfList);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[A1] characterization factors unavailable; using defaults", e);
        if (isMounted.current) setCharacterizationFactors([]);
      }

      const [fdResult, cvResult] = await Promise.allSettled([
        getFuelDensities(),
        fetchActiveCalorificValues(),
      ]);
      if (fdResult.status === "fulfilled") {
        fuelDensitiesRef.current = fdResult.value;
        if (isMounted.current) setFuelDensityCatalog(fdResult.value);
      } else {
        // eslint-disable-next-line no-console
        console.warn("[A1] fuel densities unavailable", fdResult.reason);
        fuelDensitiesRef.current = [];
        if (isMounted.current) setFuelDensityCatalog([]);
      }
      if (cvResult.status === "fulfilled") {
        calorificValuesRef.current = cvResult.value;
        if (isMounted.current) setCalorificValuesCatalog(cvResult.value);
      } else {
        // eslint-disable-next-line no-console
        console.warn("[A1] calorific values unavailable", cvResult.reason);
        calorificValuesRef.current = [];
        if (isMounted.current) setCalorificValuesCatalog([]);
      }

      setExtractionProcesses([]);

      const formResult = await getA1FormData(
        FORM_STAGE_CODES.A1_RAW_MATERIAL_SUPPLY
      );

      if (!formResult.success || !formResult.data) {
        const errorMsg = formResult.error || "Failed to load form data";
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }

      const data: FormDataResponse = formResult.data;
      setFormData(data);

      fuelSectionUnitOptionsRef.current = { density: [], calorific: [] };
      try {
        const fuelSub = data.formSubSections?.find(
          (s) => s.code === "A1_FUEL_EXTRACTION"
        );
        const [densityOpts, calorificOpts] = await Promise.all([
          loadFuelFieldUnitOptions(fuelSub, "fuelDensity_unit_extraction"),
          loadFuelFieldUnitOptions(fuelSub, "calorificValue_unit_extraction"),
        ]);
        if (isMounted.current) {
          fuelSectionUnitOptionsRef.current = {
            density: densityOpts,
            calorific: calorificOpts,
          };
        }
      } catch {
        if (isMounted.current) {
          fuelSectionUnitOptionsRef.current = { density: [], calorific: [] };
        }
      }

      // INIT ROWS = 1
      const initialRows: Record<string, number> = {};
      data.formSubSections?.forEach((s) => {
        const c = s.code!;
        initialRows[c] = 1;
      });
      setSectionRows(initialRows);

      const initValues: Record<string, string> = {};
      data.formSubSections?.flatMap(s => s.fields)
        .forEach(f => initValues[f.name] = "");
      setValues(initValues);

      if (!submissionId) {
        setBackgroundDeclaredMaterialQuantity(null);
        setBackgroundFinishedMaterialUnitSymbol(null);
        lastSavedRef.current = initValues;
        snapshotInitializedRef.current = true;
        prefillDoneRef.current = true;
        setHasSavedOnce(false);
        setIsDirty(false);
        return;
      }

      const subResult = await getA1Submission(submissionId);
      if (!subResult.success || !subResult.data) {
        setBackgroundDeclaredMaterialQuantity(null);
        setBackgroundFinishedMaterialUnitSymbol(null);
        const errorMsg = subResult.error || "Failed to load submission data";
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }

      const bgFinishedRaw =
        subResult.data.background?.finishedQuantity ??
        subResult.data.background?.finished_quantity;
      const bgFinishedNum = Number(String(bgFinishedRaw ?? "").trim());
      setBackgroundDeclaredMaterialQuantity(
        Number.isFinite(bgFinishedNum) && bgFinishedNum > 0 ? bgFinishedNum : null
      );

      try {
        const unitMap = await fetchUnitIdToSymbolMap();
        if (isMounted.current) {
          setBackgroundFinishedMaterialUnitSymbol(
            resolveFinishedQtyDenominatorSymbol(subResult.data, unitMap)
          );
        }
      } catch {
        if (isMounted.current) {
          setBackgroundFinishedMaterialUnitSymbol(
            resolveFinishedQtyDenominatorSymbol(subResult.data, {})
          );
        }
      }

      const bg = subResult.data.backgroundRawMats || [];

      const backgroundMaterialRaw =
        subResult.data?.background?.materialId ??
        subResult.data?.background?.material_id;
      const backgroundMaterialNum = Number(backgroundMaterialRaw);
      if (Number.isFinite(backgroundMaterialNum)) {
        try {
          const processes = await getExtractionProcessesByMaterial(
            backgroundMaterialNum
          );
          if (isMounted.current) setExtractionProcesses(processes);
        } catch (epErr) {
          // eslint-disable-next-line no-console
          console.warn("[A1] Failed to load extraction processes:", epErr);
          if (isMounted.current) setExtractionProcesses([]);
        }
      } else if (isMounted.current) {
        setExtractionProcesses([]);
      }

      let catalogList: { id: number; name: string }[] = [];
      if (Number.isFinite(backgroundMaterialNum)) {
        try {
          catalogList = await fetchCatalogRawMaterialsForMaterial(
            backgroundMaterialNum
          );
        } catch (catErr) {
          // eslint-disable-next-line no-console
          console.warn("[A1] Failed to load catalog raw materials:", catErr);
        }
      }

      catalogRawMaterialIdsRef.current = new Set(catalogList.map((c) => c.id));
      const nameToCatalogId = new Map<string, number>();
      for (const c of catalogList) {
        const k = normalizeRawMaterialNameKey(c.name);
        if (k && !nameToCatalogId.has(k)) nameToCatalogId.set(k, c.id);
      }
      catalogNameToIdRef.current = nameToCatalogId;

      const normalized = bg.map((r: Record<string, unknown>) => {
        const catalogId = resolveCatalogRawMaterialIdFromBackgroundRow(
          r,
          catalogList
        );

        const nameRaw =
          r.rawMaterialName ??
          r.raw_material_name ??
          r.materialName ??
          r.material_name ??
          r.name ??
          "";
        const name =
          typeof nameRaw === "string" ? nameRaw.trim() : String(nameRaw ?? "");

        const idForSelect =
          catalogId != null && catalogId > 0 ? catalogId : 0;

        return {
          id: idForSelect,
          catalogId,
          name,
          qty: String(r.qty ?? ""),
          unitId: r.unitId as number | undefined,
        };
      });

      // ✅ SOURCE OF TRUTH FOR ROWS
      setRawMaterials(normalized);

      setSectionRows((prev) => {
        const next = { ...prev };
        data.formSubSections?.forEach((s) => {
          next[s.code!] = Math.max(normalized.length, 1);
        });
        return next;
      });

      // ✅ Prefill ONLY fills values, NOT rows
      prefillFromSubmission(
        data.formSubSections || [],
        subResult.data,
        normalized,
        setValues,
        setSectionRows,
        catalogRawMaterialIdsRef.current
      );

      const hasExistingA1 =
        (Array.isArray(subResult.data?.A1_Extraction) && subResult.data.A1_Extraction.length > 0) ||
        (Array.isArray(subResult.data?.A1_Fuel) && subResult.data.A1_Fuel.length > 0) ||
        (Array.isArray(subResult.data?.A1_Energy) && subResult.data.A1_Energy.length > 0) ||
        (Array.isArray(subResult.data?.A1_Carbon) && subResult.data.A1_Carbon.length > 0);
      setHasSavedOnce(Boolean(hasExistingA1));

      prefillDoneRef.current = true;
    } catch (e: any) {
      const errorMsg = e?.response?.data?.message || e?.message || "An unexpected error occurred";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  fetchForm();
  return () => {
    isMounted.current = false;
  };
}, [submissionId]);

  // Initialize "lastSaved" snapshot after prefill values arrive
  useEffect(() => {
    if (!prefillDoneRef.current) return;
    if (snapshotInitializedRef.current) return;
    lastSavedRef.current = values;
    snapshotInitializedRef.current = true;
    setIsDirty(false);
  }, [values]);

  // Track dirty state
  useEffect(() => {
    if (!snapshotInitializedRef.current) return;
    const saved = lastSavedRef.current || {};
    setIsDirty(!shallowEqual(values, saved));
  }, [values]);

  /* ======================================================
     HANDLE CHANGE
  ====================================================== */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    try {
      const name = e.target.name;
      setErrors((prev) => clearFieldErrorsFromMap(prev, name));
      const value =
        e.target instanceof HTMLInputElement && e.target.type === "checkbox"
          ? e.target.checked
            ? "true"
            : "false"
          : e.target.value;

      // Manual edits: CF → keep value, still run energy×CF; reference/functional → block auto carbon overwrite
      if (
        name.startsWith("characterizationFactor_extraction_") ||
        name.startsWith("characterizationFactor_unit_extraction_")
      ) {
        const row = name.split("_").pop()!;
        cfManualFromUserRef.current[row] = true;
      }
      if (
        name.startsWith("embodiedCarbon_referenceQty_extraction_") ||
        name.startsWith("embodiedCarbon_functionalUnit_extraction_")
      ) {
        const row = name.split("_").pop()!;
        embodiedCarbonDerivedManualRef.current[row] = true;
      }

      setValues((prev) => {
        const densityKey = pairedFuelDensityValueKey(name);
        if (densityKey) {
          const updated = applyFuelDensityUnitFieldChange(
            prev,
            name,
            value,
            densityKey,
            fuelSectionUnitOptionsRef.current.density
          );
          computeAndSetEnergySummary();
          autoSetCharacterizationFactor(updated);
          return updated;
        }

        const next = { ...prev, [name]: value };

        // IMPORTANT: deterministic calculation order (carbon follows embodied energy via useEffect)
        computeAndSetEnergySummary();
        autoSetCharacterizationFactor(next);

        // Handle location fetching (same as PublicationData)
        if (
          name.startsWith("extractionLatitude_") ||
          name.startsWith("extractionLongitude_")
        ) {
          const parts = name.split("_");
          const code = parts.slice(1, -1).join("_");
          const row = parts.pop()!;
          
          const latKey = `extractionLatitude_${code}_${row}`;
          const lngKey = `extractionLongitude_${code}_${row}`;
          const latStr = next[latKey];
          const lngStr = next[lngKey];

          // Only fetch if both lat and lng are available
          if (latStr && lngStr) {
            const lat = Number(latStr);
            const lng = Number(lngStr);

            // Format validation: check if valid numbers and within valid ranges
            const isValidLat =
              Number.isFinite(lat) && lat >= -90 && lat <= 90;
            const isValidLng =
              Number.isFinite(lng) && lng >= -180 && lng <= 180;

            // Only fetch if both are valid
            if (isValidLat && isValidLng) {
              reverseGeocode(lat, lng).then((data) => {
                const address = data?.address || {};

                setValues((p) => ({
                  ...p,
                  [`extractionState_${code}_${row}`]:
                    address.state || address.region || address.city || "",
                  [`extractionDistrict_${code}_${row}`]:
                    address.district ||
                    address.city_district ||
                    address.county ||
                    address.state_district ||
                    "N/A",
                  [`extractionPincode_${code}_${row}`]:
                    address.postcode || "",
                }));
              }).catch((err) => {
                console.error("Reverse geocoding error:", err);
                // Don't show error to user, just log it
              });
            } else {
              // Clear state/district if format is invalid
              setValues((p) => ({
                ...p,
                [`extractionState_${code}_${row}`]: "",
                [`extractionDistrict_${code}_${row}`]: "",
              }));
            }
          }
        }

        const fuelMixParsed = parseFuelMixFieldName(name);
        if (fuelMixParsed) {
          const { code, row } = fuelMixParsed;
          const densityKey = `fuelDensity_extraction_${code}_${row}`;
          const densityUnitKey = `fuelDensity_unit_extraction_${code}_${row}`;
          const calorificKey = `calorificValue_extraction_${code}_${row}`;
          const calorificUnitKey = `calorificValue_unit_extraction_${code}_${row}`;
          const mixStr = String(value ?? "").trim();

          if (!mixStr) {
            next[densityKey] = "";
            next[densityUnitKey] = "";
            next[calorificKey] = "";
            next[calorificUnitKey] = "";
          } else {
            const mixLabel = getFuelMixDropdownLabel(formData, mixStr);

            const rowData = findFuelDensityForMix(
              mixStr,
              mixLabel,
              fuelDensitiesRef.current
            );
            if (rowData) {
              const densityOpts = fuelSectionUnitOptionsRef.current.density;
              const { densityStr, unitVal } =
                fuelDensityValueAndUnitFromCatalogRow(rowData, densityOpts);
              next[densityKey] = densityStr;
              next[densityUnitKey] = unitVal;
            } else {
              next[densityKey] = "";
              next[densityUnitKey] = "";
            }

            const cvRow = findCalorificValueForMix(
              mixStr,
              mixLabel,
              calorificValuesRef.current
            );
            const cvText =
              cvRow?.calorificValue != null
                ? String(cvRow.calorificValue).trim()
                : "";
            if (cvRow && cvText) {
              next[calorificKey] = cvText;
              const unitVal = pickCalorificUnitFromOptions(
                cvRow.unit ?? "",
                fuelSectionUnitOptionsRef.current.calorific,
                unitIdByTokenRef.current
              );
              next[calorificUnitKey] = unitVal;
            } else {
              next[calorificKey] = "";
              next[calorificUnitKey] = "";
            }
          }
        }

        return next;
      });
    } catch (err) {
      console.error("❌ handleChange failed:", err);
    }
  };

  /* ======================================================
     SAVE
  ====================================================== */

const buildPayloadAndSave = async () => {
  if (!formData || !submissionId) return false;

  const sections: FormSubSection[] = formData.formSubSections || [];
  const formErrors = validateA1Form({
    sections,
    sectionRows,
    values,
    formData,
    fuelDensityCatalog: fuelDensitiesRef.current,
    calorificValuesCatalog: calorificValuesRef.current,
  });
  if (Object.keys(formErrors).length > 0) {
    setErrors(formErrors);
    toast.error(
      getFirstFormError(formErrors) ?? "Please fix the highlighted fields."
    );
    return false;
  }
  setErrors({});

  const toNumberOrNull = (v: any) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const toUnitIdOrNull = (v: any) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
    const token = String(v).trim().toLowerCase();
    return unitIdByTokenRef.current[token] ?? null;
  };

  const declared = backgroundDeclaredMaterialQuantity;
  const embodiedEnergyA1PerDeclared =
    declared != null && declared > 0
      ? Math.round((energySummaryMj / declared) * 1000) / 1000
      : 0;

  const payload: any = {
    extraction: [],
    fuel: [],
    energy: [],
    carbon: [],
    energySummary: {
      embodiedEnergyA1: embodiedEnergyA1PerDeclared,
      unit: perFinishedEnergyUnitValue(backgroundFinishedMaterialUnitSymbol),
    },
  };

  const getVal = (base: string, code: string, i: number) =>
    values?.[`${base}_${code}_row${i}`] ?? null;

  const getLabel = (base: string, code: string, i: number) =>
    values?.[`${base}_${code}_row${i}_label`] ?? null;

  const rawMaterialFkForRow = (
    rowIndex: number,
    formVal: unknown
  ): number | null => {
    const rm = rawMaterials[rowIndex];
    if (rm?.catalogId != null && rm.catalogId > 0) return rm.catalogId;
    const n = toNumberOrNull(formVal);
    const catalogIds = catalogRawMaterialIdsRef.current;
    if (n != null && n > 0 && catalogIds.has(n)) return n;
    if (rm?.name) {
      const byName = catalogNameToIdRef.current.get(
        normalizeRawMaterialNameKey(rm.name)
      );
      if (byName != null) return byName;
    }
    return null;
  };

  (formData.formSubSections || []).forEach((sec) => {
    const code = sec.code;
    if (!code) return;

    const rows = sectionRows?.[code] || 1;

    /* ===================== A1 RAW EXTRACTION ===================== */
    if (code === "A1_RAW_EXTRACTION") {
      const extractionProcessFieldName =
        sec.fields.find((f) => f.type === "EXTRACTION_PROCESS")?.name ??
        "RawMaterialExtractionProcesses";
      for (let i = 0; i < rows; i++) {
        payload.extraction.push({
          rawMaterialId: rawMaterialFkForRow(
            i,
            getVal("rawMaterial_extraction", code, i)
          ),
          rawMaterialName: getLabel("rawMaterial_extraction", code, i),
          latitude: getVal("extractionLatitude", code, i),
          longitude: getVal("extractionLongitude", code, i),
          state: getVal("extractionState", code, i),
          district: getVal("extractionDistrict", code, i),
          pincode: getVal("extractionPincode", code, i),
          extractionStatus: getVal("extractionStatus", code, i),
          extractionProcess: getVal(extractionProcessFieldName, code, i),
        });
      }
    }

    /* ===================== A1 FUEL ===================== */
    if (code === "A1_FUEL_EXTRACTION") {
      for (let i = 0; i < rows; i++) {
        payload.fuel.push({
          rawMaterialId: rawMaterialFkForRow(
            i,
            getVal("rawMaterial_extraction_details", code, i)
          ),
          rawMaterialName: getLabel(
            "rawMaterial_extraction_details",
            code,
            i
          ),
          fuelMix: getVal("fuelMix_extraction", code, i),
          quantity: getVal("fuelQuantity_extraction", code, i),
          unitId: toUnitIdOrNull(
            getVal("fuelQuantity_unit_extraction", code, i)
          ),
          fuelDensity: getVal("fuelDensity_extraction", code, i),
          fuelDensityUnit: toUnitIdOrNull(
            getVal("fuelDensity_unit_extraction", code, i)
          ),
          calorificValue: getVal("calorificValue_extraction", code, i),
          calorificValueUnit: toUnitIdOrNull(
            getVal("calorificValue_unit_extraction", code, i)
          ),
        });
      }
    }

    /* ===================== A1 ENERGY ===================== */
    if (code === "A1_EMBODIED_ENERGY") {
      for (let i = 0; i < rows; i++) {
        payload.energy.push({
          rawMaterialId: rawMaterialFkForRow(
            i,
            getVal("rawMaterialsExtractionProcesses", code, i)
          ),
          rawMaterialName: getLabel(
            "rawMaterialsExtractionProcesses",
            code,
            i
          ),
          embodiedEnergy: getVal("embodiedEnergy_extraction", code, i),
          unitId: toUnitIdOrNull(
            getVal("embodiedEnergy_unit_extraction", code, i)
          ),
        });
      }
    }

    /* ===================== A1 CARBON ===================== */
    if (code === "A1_EMBODIED_CARBON") {
      for (let i = 0; i < rows; i++) {
        const rawCfUnit = getVal("characterizationFactor_unit_extraction", code, i);
        const cfUnitId = toUnitIdOrNull(rawCfUnit);
        payload.carbon.push({
          rawMaterialId: rawMaterialFkForRow(
            i,
            getVal("rawMaterialsEmbodiedCarbonDetails", code, i)
          ),
          rawMaterialName: getLabel(
            "rawMaterialsEmbodiedCarbonDetails",
            code,
            i
          ),
          characterizationFactor: getVal(
            "characterizationFactor_extraction",
            code,
            i
          ),
          characterizationFactorUnit: cfUnitId,
          referenceQty: formatEmbodiedCarbonReferenceQtyFromUnknown(
            getVal("embodiedCarbon_referenceQty_extraction", code, i)
          ),
          referenceQtyUnitId: toUnitIdOrNull(
            getVal("embodiedCarbon_referenceQty_unit_extraction", code, i)
          ),
          functionalUnitCarbon: (() => {
            const formatted = formatEmbodiedCarbonFunctionalUnitFromUnknown(
              getVal("embodiedCarbon_functionalUnit_extraction", code, i)
            );
            return formatted || "0";
          })(),
          functionalUnitCarbonUnitId: resolveEmbodiedCarbonFunctionalUnitForSave(
            getVal(
              "embodiedCarbon_functionalUnit_unit_extraction",
              code,
              i
            ),
            unitIdByTokenRef.current,
            backgroundFinishedMaterialUnitSymbol,
            resolveKgCo2eUnitId(unitIdByTokenRef.current)
          ),
        });
      }
    }
  });

  console.log("✅ FINAL A1 PAYLOAD", payload);

  try {
    const result = await saveA1Details(submissionId, payload);
    
    if (result.success) {
      toast.success(result.message || "A1 data saved successfully!", {
        style: {
          backgroundColor: "#10b981",
          color: "#ffffff",
          fontSize: "16px",
          fontWeight: "500",
        },
      });
      lastSavedRef.current = values;
      snapshotInitializedRef.current = true;
      setHasSavedOnce(true);
      setIsDirty(false);
      return true;
    } else {
      const errorMsg = result.error || "Failed to save A1 data";
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }
  } catch (err: any) {
    console.error("Save error:", err);
    const errorMessage =
      err?.response?.data?.message ||
      err?.message ||
      "An unexpected error occurred while saving";
    setError(errorMessage);
    toast.error(errorMessage);
    return false;
  }
};


  return {
    formData,
    loading,
    error,
    errors,
    rawMaterials,
    extractionProcesses,
    sectionRows,
    values,
    setValues,
    hasSavedOnce,
    isDirty,
    handleChange,
    computeAndSetEnergySummary,
    energySummaryMj,
    energyPerDeclaredFinishedMaterialMj,
    embodiedCarbonTotalKg: embodiedCarbonRowSum,
    embodiedCarbonPerFinishedKg,
    perFinishedEnergyUnitLabel: perFinishedEnergyUnitLabelDisplay,
    perFinishedCarbonUnitLabel: perFinishedCarbonUnitLabelDisplay,
    backgroundFinishedMaterialUnitSymbol,
    buildPayloadAndSave,
    fuelDensityCatalog,
    calorificValuesCatalog,
  };
}